/**
 * 学员端 - U币明细页面
 * 
 * 功能:
 * 1. 分页展示U币流水记录
 * 2. 支持按收支分类筛选
 * 3. 显示每笔流水的详细信息(时间、类型、变动、余额)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractBeijingTime } from '../utils/timeUtils';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface Transaction {
  id: string;
  user_id: string;
  role: string;
  points_change: number;
  point_type: string;
  source_type: string;
  source_id: string | null;
  source_detail: any;
  balance_after: number;
  expire_at: string | null;
  operator_id: string | null;
  operator_role: string | null;
  remark: string | null;
  created_at: string;
}

// U币类型标签映射 - 统一为"支出"和"收入"
const POINT_TYPE_LABELS: Record<string, string> = {
  purchase_rebate: '收入',
  error_correction: '收入',
  activity_reward: '收入',
  teacher_gift: '收入',
  deduction: '支出',
  expiration: '支出',
  refund_rollback: '收入',
  admin_adjust: '收入',
};

// U币类型颜色映射 - 收入用绿色,支出用红色
const POINT_TYPE_COLORS: Record<string, string> = {
  purchase_rebate: 'bg-green-100 text-green-700',
  error_correction: 'bg-green-100 text-green-700',
  activity_reward: 'bg-green-100 text-green-700',
  teacher_gift: 'bg-green-100 text-green-700',
  deduction: 'bg-red-100 text-red-700',
  expiration: 'bg-red-100 text-red-700',
  refund_rollback: 'bg-green-100 text-green-700',
  admin_adjust: 'bg-green-100 text-green-700',
};

const PointTransactions: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');

  const pageSize = 20;

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchTransactions();
    }
  }, [userId, page, filterType]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUserId(user.id);
    } else {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const body: any = {
        action: 'get_transactions',
        user_id: userId!,
        role: 'student',
        page,
        page_size: pageSize,
      };

      // 根据筛选条件设置 point_type
      if (filterType === 'income') {
        // 收入类型: purchase_rebate, error_correction, activity_reward, teacher_gift, refund_rollback, admin_adjust
        body.point_types = ['purchase_rebate', 'error_correction', 'activity_reward', 'teacher_gift', 'refund_rollback', 'admin_adjust'];
      } else if (filterType === 'expense') {
        // 支出类型: deduction, expiration
        body.point_types = ['deduction', 'expiration'];
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (result.success) {
        setTransactions(result.transactions || []);
        setTotalPages(result.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">U币明细</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 筛选器 - 简化为全部、收入、支出 */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <FilterChip
              label="全部"
              active={filterType === 'all'}
              onClick={() => setFilterType('all')}
            />
            <FilterChip
              label="收入"
              active={filterType === 'income'}
              onClick={() => setFilterType('income')}
            />
            <FilterChip
              label="支出"
              active={filterType === 'expense'}
              onClick={() => setFilterType('expense')}
            />
          </div>
        </div>

        {/* 流水列表 */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">加载中...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <div className="text-gray-500">暂无U币流水记录</div>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <TransactionItem key={tx.id} transaction={tx} formatDate={formatDate} />
            ))}
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              上一页
            </button>
            <span className="text-sm text-gray-600">
              第 {page} / {totalPages} 页
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// 筛选芯片组件
const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
        active
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
};

// 生成交易描述 - 详细记载U币动向的具体原因
const getTransactionDescription = (transaction: Transaction): string => {
  const { source_type, point_type, source_detail, remark, points_change } = transaction;
  const typeLabel = POINT_TYPE_LABELS[point_type] || point_type;
  const isPositive = points_change > 0;
  const absPoints = Math.abs(points_change);

  // 优先使用 remark 作为描述基础
  if (remark) {
    // admin_adjust: remark 就是原因
    if (source_type === 'admin_adjust') {
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分：${remark}`
        : `管理员扣减${typeLabel}${absPoints}分：${remark}`;
    }
    // gift: 教师赠送学员
    if (source_type === 'gift') {
      return remark.includes('教师') || remark.includes('赠送')
        ? remark
        : `教师赠送${absPoints}分：${remark}`;
    }
    // 其他类型直接用 remark
    return remark;
  }

  // 没有 remark 时，根据 source_type 和 source_detail 生成描述
  switch (source_type) {
    case 'admin_adjust':
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分`
        : `管理员扣减${typeLabel}${absPoints}分`;
    case 'gift':
      return `教师赠送${absPoints}分`;
    case 'purchase_rebate':
      return `购课返利${absPoints}分`;
    case 'error_correction':
      return `纠错奖励${absPoints}分`;
    case 'order_deduction':
      return `U币抵扣订单${absPoints}分`;
    case 'refund_rollback':
      return `退款回滚${absPoints}分`;
    case 'expiration':
      return `U币过期清零${absPoints}分`;
    default:
      return isPositive
        ? `增加${typeLabel}${absPoints}分`
        : `扣减${typeLabel}${absPoints}分`;
  }
};

// 流水项组件 - 两行布局：上行描述+U币变动，下行时间+余额
const TransactionItem: React.FC<{
  transaction: Transaction;
  formatDate: (date: string) => string;
}> = ({ transaction, formatDate }) => {
  const isPositive = transaction.points_change > 0;
  const description = getTransactionDescription(transaction);

  return (
    <div className="bg-white rounded-xl px-4 py-3 shadow-sm">
      {/* 上行：类型标签 + 描述 + U币变动 */}
      <div className="flex items-center gap-2">
        {/* 类型标签 - 统一显示"收入"或"支出" */}
        <span
          className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            POINT_TYPE_COLORS[transaction.point_type] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {POINT_TYPE_LABELS[transaction.point_type] || transaction.point_type}
        </span>

        {/* 详细描述 */}
        <span className="text-sm text-gray-700 break-words" title={description}>
          {description}
        </span>

        {/* U币变动 */}
        <div className={`text-sm font-bold whitespace-nowrap ml-auto ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}{transaction.points_change}
        </div>
      </div>

      {/* 下行：操作时间 + 余额 */}
      <div className="flex items-center gap-3 mt-1">
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {formatDate(transaction.created_at)}
        </div>
        <div className="text-xs text-orange-500 font-medium whitespace-nowrap ml-auto">
          余额: {transaction.balance_after}
        </div>
      </div>
    </div>
  );
};

export default PointTransactions;
